import React, { useState, useEffect, useMemo, memo, useRef, useCallback } from 'react';
import { X, MapPin, Clock, MessageSquare, Search, Navigation, Calendar, AlertCircle, CheckCircle, PlayCircle, Save, RefreshCw, ChevronRight } from 'lucide-react';
import axios from 'axios';
import VoiceRecorder from './VoiceRecorder';
import OverdueResolutionModal from './OverdueResolutionModal';
import AddVisitModal from './AddVisitModal';
import { sanitize } from '../utils/sanitize';
import { debug } from '../utils/log';
import { formatKm } from '../utils/formatters';

// OverdueCard — dedicated card for the Overdue tab.
// Intentionally separate from the main visit card to avoid coupling overdue-specific
// logic (customer_confirmed nudge, days-overdue calculation) into the shared card path.
const OverdueCard = ({
    item,
    index,
    getDaysOverdue,
    onItemClick,
    onOpenOverdueModal,
    canDo,
}) => {
    const daysOverdue = getDaysOverdue(item.scheduled_date);

    return (
        <div
            data-testid="overdue-card"
            data-visit-id={item.id}
            data-entity-global-id={item.entity_global_id}
            className="bg-gray-800 border border-red-800/50 rounded-xl p-3.5 cursor-pointer shadow-md"
            onClick={() => onItemClick && onItemClick(item)}
        >
            {/* Entity name + distance */}
            <div className="flex items-start justify-between mb-1.5">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                        {index + 1}
                    </div>
                    <span className="font-bold text-sm text-gray-100">
                        {sanitize(item.entity_name)}
                    </span>
                </div>
                {formatKm(item.distance) && (
                    <span className="text-xs text-blue-400 font-semibold flex-shrink-0">
                        {formatKm(item.distance)}
                    </span>
                )}
            </div>

            {/* Days overdue (red) + scheduled date + bumped count (gray) */}
            <div className="flex flex-wrap gap-1.5 mb-2">
                {daysOverdue > 0 && (
                    <span className="bg-red-900/40 text-red-300 rounded-full text-[11px] px-2 py-0.5 font-semibold">
                        {daysOverdue} {daysOverdue === 1 ? 'day' : 'days'} overdue
                    </span>
                )}
                {item.scheduled_date && (
                    <span className="bg-gray-700 text-gray-300 rounded-full text-[11px] px-2 py-0.5">
                        {new Date(item.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                )}
                {item.rescheduled_count > 0 && (
                    <span className="bg-gray-700 text-gray-400 rounded-full text-[11px] px-2 py-0.5">
                        Bumped {item.rescheduled_count}&times;
                    </span>
                )}
                {item.customer_confirmed && (
                    <span className="bg-yellow-900/40 text-yellow-300 rounded-full text-[11px] px-2 py-0.5 font-semibold">
                        Confirmed appt
                    </span>
                )}
            </div>

            {/* customer_confirmed nudge banner — only when this was a booked appointment */}
            {item.customer_confirmed === true && (
                <div
                    className="bg-yellow-900/30 border border-yellow-700/50 rounded-md px-2.5 py-2 text-xs text-yellow-300 mb-2.5 leading-snug"
                    onClick={(e) => e.stopPropagation()}
                >
                    This was a confirmed appointment. The customer may still be expecting you.
                </div>
            )}

            {/* CTA buttons — stop propagation so card click (open detail) doesn't fire */}
            <div
                className="flex gap-1.5 mt-1.5"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Mark as Completed — Scenario 1 */}
                <button
                    onClick={() => onOpenOverdueModal(item, 'complete')}
                    data-testid="overdue-action-complete"
                    className="flex-1 flex items-center justify-center gap-1 px-2.5 py-2 bg-green-700 hover:bg-green-600 text-white border-none rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                    style={{ minHeight: '44px' }}
                >
                    <CheckCircle size={13} />
                    Mark Completed
                </button>

                {/* Didn't Happen — Scenario 2a or 2b depending on customer_confirmed.
                    Hidden only if the rep may neither reschedule nor cancel overdue visits. */}
                {(!canDo || canDo('reschedule', item) || canDo('cancel', item)) && (
                    <button
                        onClick={() => onOpenOverdueModal(
                            item,
                            item.customer_confirmed ? 'resolve-scheduled' : 'resolve-adhoc'
                        )}
                        data-testid="overdue-action-didnt-happen"
                        className="flex-1 flex items-center justify-center gap-1 px-2.5 py-2 bg-orange-700 hover:bg-orange-600 text-white border-none rounded-lg text-xs font-semibold cursor-pointer transition-colors"
                        style={{ minHeight: '44px' }}
                    >
                        <X size={13} />
                        Didn't Happen
                    </button>
                )}
            </div>
        </div>
    );
};

const TodaysVisits = ({ visitList, overdueList, onRemove, onClear, onSync, onClose, onVoiceUploadSuccess, onItemClick, onCheckIn, onCompleteVisit, onFilterChange, viewMode, onViewModeChange, undoableActions, onUndo, onOptimizeRoute, isOptimizing, optimizedRoute, showRouteOnMap, onToggleRouteOnMap, onReschedule, overdueCount, parentFilterMode, onLeadStatusChange, visitsLoading, visitPermissions = null, panelMode = false, hideInternalFilters = false, geofenceActive = false }) => {
    const [feedbackData, setFeedbackData] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [userLocation, setUserLocation] = useState(null);
    const [visitNotes, setVisitNotes] = useState({}); // Track notes by visit_id
    const [expandedNotes, setExpandedNotes] = useState({}); // Track which notes sections are expanded
    const [saveStatus, setSaveStatus] = useState({}); // Track save status by visit_id ('saving', 'saved', 'error')
    const [filterMode, setFilterMode] = useState('today'); // Strict 3-filter contract: 'today' | 'overdue' | 'scheduled'
    const [undoCountdowns, setUndoCountdowns] = useState({}); // Track countdown timers for undo (30s window)
    const [rescheduleVisitId, setRescheduleVisitId] = useState(null); // Which visit is being rescheduled
    const [rescheduleDate, setRescheduleDate] = useState(''); // Date picker value for rescheduling
    const [rescheduleModalVisit, setRescheduleModalVisit] = useState(null); // Customer-confirmed visit being rescheduled via the full modal
    const [showClearConfirm, setShowClearConfirm] = useState(false); // Confirmation dialog for clear
    const [showSearch, setShowSearch] = useState(false); // Collapsible search bar
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const carouselRef = useRef(null);
    const [skipVisitId, setSkipVisitId] = useState(null); // Which visit is being skipped
    const [skipReason, setSkipReason] = useState(''); // Selected skip reason
    const [skipStep, setSkipStep] = useState('reason'); // 'reason' | 'comm'
    const [cancelVisitId, setCancelVisitId] = useState(null); // Which visit is being cancelled
    const [cancelReason, setCancelReason] = useState(''); // Selected cancel reason
    const [cancelStep, setCancelStep] = useState('reason'); // 'reason' | 'comm'
    const [completeError, setCompleteError] = useState({}); // Inline error by visit_id for completion validation
    // Overdue resolution modal state — controls which scenario modal is open and for which visit
    const [overdueModal, setOverdueModal] = useState({
        open: false,
        scenario: null,  // 'complete' | 'resolve-adhoc' | 'resolve-scheduled' | 'bulk-absent'
        visit: null,
        absentDate: null,
        absentVisitCount: 0,
    });

    // Sync local filterMode when parent changes it programmatically (e.g., after accepting from briefing)
    // filterMode intentionally excluded to avoid infinite loop (parent sets → effect fires → sets again)
    useEffect(() => {
        if (parentFilterMode && parentFilterMode !== filterMode) {
            setFilterMode(parentFilterMode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentFilterMode]);

    // Get user's current location
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                (error) => {
                    console.warn("Location access denied:", error);
                    // Continue without location - will just show unsorted list
                }
            );
        }
    }, []);

    // Calculate distance between two points (Haversine formula)
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Earth's radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    // Derived: sort by proximity and filter by search term.
    // useMemo avoids the extra render cycle that the old useEffect+setState pattern caused
    // (effect fires after paint → setState → second render). Now it computes synchronously
    // during render, only when visitList, userLocation, or searchTerm change.
    const sortedVisits = useMemo(() => {
        if (!visitList || visitList.length === 0) return [];

        let processed = [...visitList];

        // History is sorted by scheduled_date descending (most recent first) —
        // distance sort doesn't apply.
        if (viewMode === 'history') {
            processed.sort((a, b) => {
                const aDate = a.scheduled_date || '';
                const bDate = b.scheduled_date || '';
                return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
            });
        } else if (userLocation) {
            // Add distance if user location available (route/today view only)
            processed = processed.map(item => ({
                ...item,
                distance: calculateDistance(
                    userLocation.lat,
                    userLocation.lng,
                    item.entity_latitude,
                    item.entity_longitude
                )
            }));
            // Sort by distance (nearest first)
            processed.sort((a, b) => a.distance - b.distance);
        }

        // Apply search filter
        if (searchTerm) {
            processed = processed.filter(item =>
                item.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.entity_address?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return processed;
    // calculateDistance is defined inline in the component body and is stable (no deps),
    // so it intentionally does not need to be in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visitList, userLocation, searchTerm]);

    // Derived: sort overdueList by proximity — same logic as sortedVisits.
    // Rendered only when filterMode === 'overdue' (dedicated overdue tab).
    const sortedOverdueList = useMemo(() => {
        if (!overdueList || overdueList.length === 0) return [];

        let processed = [...overdueList];

        if (userLocation) {
            processed = processed.map(item => ({
                ...item,
                distance: calculateDistance(
                    userLocation.lat,
                    userLocation.lng,
                    item.entity_latitude,
                    item.entity_longitude
                )
            }));
            processed.sort((a, b) => a.distance - b.distance);
        }

        if (searchTerm) {
            processed = processed.filter(item =>
                item.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.entity_address?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return processed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overdueList, userLocation, searchTerm]);

    // Initialize notes from visitList
    useEffect(() => {
        if (visitList && visitList.length > 0) {
            const notes = {};
            visitList.forEach(visit => {
                if (visit.id && visit.notes !== undefined) {
                    notes[visit.id] = visit.notes || '';
                }
            });
            setVisitNotes(notes);
        }
    }, [visitList]);

    // Manage countdown timers for undoable actions (30-second window)
    useEffect(() => {
        if (!undoableActions || undoableActions.size === 0) {
            setUndoCountdowns({});
            return;
        }

        // Update countdown every second
        const interval = setInterval(() => {
            const newCountdowns = {};
            undoableActions.forEach((action, visitId) => {
                const elapsed = Date.now() - action.timestamp;
                const remaining = Math.max(0, 30 - Math.floor(elapsed / 1000));
                if (remaining > 0) {
                    newCountdowns[visitId] = remaining;
                }
            });
            setUndoCountdowns(newCountdowns);
        }, 1000);

        return () => clearInterval(interval);
    }, [undoableActions]);

    useEffect(() => {
        if (onSync) {
            // This is just to satisfy the linter if onSync is used in an effect
            // But actually we use it in handleSaveRoute in App.js
        }
    }, [onSync]);

    // Save notes to backend
    const handleSaveNotes = async (visitId, notes) => {
        try {
            setSaveStatus(prev => ({ ...prev, [visitId]: 'saving' }));

            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

            // Use axios which has the Authorization header set globally by App.js
            await axios.patch(`${API_BASE_URL}/api/visits/${visitId}`, {
                notes: notes
            });

            setSaveStatus(prev => ({ ...prev, [visitId]: 'saved' }));
            debug(`✅ Notes saved for visit ${visitId}`);

            // Clear saved status after 2 seconds
            setTimeout(() => {
                setSaveStatus(prev => ({ ...prev, [visitId]: null }));
            }, 2000);
        } catch (error) {
            console.error('❌ Failed to save notes:', error);
            if (error.response?.status === 401) {
                console.error('Authentication error - token may be expired');
            }
            setSaveStatus(prev => ({ ...prev, [visitId]: 'error' }));

            // Clear error status after 3 seconds
            setTimeout(() => {
                setSaveStatus(prev => ({ ...prev, [visitId]: null }));
            }, 3000);
        }
    };

    // Handle notes change (without auto-save)
    const handleNotesChange = (visitId, newNotes) => {
        setVisitNotes(prev => ({ ...prev, [visitId]: newNotes }));
    };

    // Handle skip visit — step 1: reason chosen → step 2: comm prompt → submit
    const handleSkipReasonNext = () => {
        if (!skipReason) return;
        setSkipStep('comm');
    };

    const handleSkipSubmit = async (visitId) => {
        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
            const visit = visitList.find(v => v.id === visitId);
            await axios.patch(`${API_BASE_URL}/api/visits/${visitId}`, {
                visit_state: 'skipped',
                skip_reason: skipReason
            });
            setSkipVisitId(null);
            setSkipReason('');
            setSkipStep('reason');
            // Revert lead pin/badge back to 'new'
            if (visit?.entity_global_id?.startsWith('lead_') && onLeadStatusChange) {
                onLeadStatusChange(visit.entity_global_id, 'new');
            }
            if (onSync) onSync();
        } catch (error) {
            console.error('Failed to skip visit:', error);
        }
    };

    // Handle cancel visit — step 1: reason chosen → step 2: comm prompt → submit
    const handleCancelReasonNext = () => {
        if (!cancelReason) return;
        setCancelStep('comm');
    };

    const handleCancelSubmit = async (visitId) => {
        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
            const visit = visitList.find(v => v.id === visitId);
            await axios.patch(`${API_BASE_URL}/api/visits/${visitId}`, {
                visit_state: 'cancelled',
                skip_reason: cancelReason
            });
            setCancelVisitId(null);
            setCancelReason('');
            setCancelStep('reason');
            // Revert lead pin/badge back to 'new'
            if (visit?.entity_global_id?.startsWith('lead_') && onLeadStatusChange) {
                onLeadStatusChange(visit.entity_global_id, 'new');
            }
            if (onSync) onSync();
        } catch (error) {
            console.error('Failed to cancel visit:', error);
        }
    };

    // Handle complete visit with notes/voice validation
    const handleCompleteWithValidation = (visitId, itemGlobalId) => {
        const visit = visitList.find(v => v.id === visitId);
        const hasNotes = visitNotes[visitId] || visit?.notes;
        const hasVoice = !!(feedbackData[itemGlobalId]);
        if (!hasNotes && !hasVoice) {
            setExpandedNotes(prev => ({ ...prev, [visitId]: true }));
            setCompleteError(prev => ({ ...prev, [visitId]: true }));
            return;
        }
        setCompleteError(prev => ({ ...prev, [visitId]: false }));
        if (onCompleteVisit) onCompleteVisit(visitId);
    };

    // Skip reason options
    const SKIP_REASONS = [
        'Store closed',
        'No decision maker available',
        'Bad weather / road conditions',
        'Running late / time constraint',
        'Higher priority visit',
        'Other'
    ];

    // Cancel reason options
    const CANCEL_REASONS = [
        'Customer requested cancellation',
        'Store permanently closed',
        'Duplicate visit',
        'No longer relevant',
        'Manager instruction',
        'Other'
    ];

    // Per-salesperson visit-action permission gating. Mirrors the backend
    // (visits.py _enforce_visit_permission) but is only a UX layer — the API is
    // authoritative. A missing key or absent perms object means allowed.
    const visitWindow = (scheduledDate) => {
        if (!scheduledDate) return 'today';
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const d = new Date(scheduledDate + 'T00:00:00');
        if (d < today) return 'overdue';
        if (d.getTime() === today.getTime()) return 'today';
        return 'scheduled';
    };
    const canDo = (action, item) => {
        const key = `visit_perm_${action}_${visitWindow(item?.scheduled_date)}`;
        return visitPermissions?.[key] !== false;
    };

    // Format distance for display
    // Calculate days since item was added to list
    const getDaysSinceAdded = (claimedAt) => {
        if (!claimedAt) return null;
        const added = new Date(claimedAt);
        const now = new Date();
        const diffTime = Math.abs(now - added);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Check if visit is stale (>7 days old)
    const isStale = (claimedAt) => {
        const days = getDaysSinceAdded(claimedAt);
        return days !== null && days > 7;
    };

    // Calculate days overdue from scheduled_date
    const getDaysOverdue = (scheduledDate) => {
        if (!scheduledDate) return 0;
        const scheduled = new Date(scheduledDate + 'T00:00:00'); // Parse as local date
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const diffTime = now - scheduled;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    // Get overdue severity badge styling
    const getOverdueBadge = (daysOverdue) => {
        if (daysOverdue > 14) {
            return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', label: `${daysOverdue}d overdue` };
        } else if (daysOverdue > 7) {
            return { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', label: `${daysOverdue}d overdue` };
        } else if (daysOverdue >= 1) {
            return { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', label: `${daysOverdue}d overdue` };
        }
        return null;
    };

    // Handle reschedule submission
    const handleRescheduleSubmit = async (visitId) => {
        if (!rescheduleDate || !onReschedule) return;
        await onReschedule(visitId, rescheduleDate);
        setRescheduleVisitId(null);
        setRescheduleDate('');
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (date.toDateString() === today.toDateString()) {
            return "Today";
        } else if (date.toDateString() === tomorrow.toDateString()) {
            return "Tomorrow";
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    // Get visit state badge styling
    const getVisitStateBadge = (visitState) => {
        const states = {
            'planned': {
                bg: 'bg-yellow-100',
                text: 'text-yellow-700',
                label: '📅 Planned',
                icon: Calendar
            },
            'checked_in': {
                bg: 'bg-blue-100',
                text: 'text-blue-700',
                label: '✓ Checked In',
                icon: PlayCircle
            },
            'completed': {
                bg: 'bg-green-100',
                text: 'text-green-700',
                label: '✅ Completed',
                icon: CheckCircle
            },
            'skipped': {
                bg: 'bg-gray-100',
                text: 'text-gray-700',
                label: '⊘ Skipped',
                icon: X
            },
            'cancelled': {
                bg: 'bg-red-100',
                text: 'text-red-700',
                label: 'Cancelled',
                icon: AlertCircle
            }
        };
        return states[visitState] || states['planned'];
    };

    // Open navigation app
    const handleNavigate = (item) => {
        const lat = item.entity_latitude;
        const lng = item.entity_longitude;

        // Detect platform and open appropriate maps app
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const url = isIOS
            ? `maps://maps.apple.com/?daddr=${lat},${lng}`
            : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

        window.open(url, '_blank');
    };

    const handleVoiceUpload = async (itemGlobalId, visitId, data) => {
        // Store feedback data by global_id
        setFeedbackData(prev => ({
            ...prev,
            [itemGlobalId]: data.extraction
        }));

        // Auto-save a note so the backend notes-required check passes on completion
        if (visitId && !visitNotes[visitId]) {
            const voiceNote = 'Voice memo recorded';
            try {
                const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
                await axios.patch(`${API_BASE_URL}/api/visits/${visitId}`, { notes: voiceNote });
                setVisitNotes(prev => ({ ...prev, [visitId]: voiceNote }));
            } catch (err) {
                console.error('Failed to auto-save voice note:', err);
            }
        }

        // Notify parent component if callback provided
        if (onVoiceUploadSuccess) {
            onVoiceUploadSuccess(data);
        }
    };

    // Open the overdue resolution modal for a specific visit + scenario
    const openOverdueModal = (visit, scenario) => {
        setOverdueModal({ open: true, scenario, visit, absentDate: null, absentVisitCount: 0 });
    };

    // Open the bulk-absent modal for a shared date group
    const openBulkAbsentModal = (date, count) => {
        setOverdueModal({ open: true, scenario: 'bulk-absent', visit: null, absentDate: date, absentVisitCount: count });
    };

    // Called by OverdueResolutionModal after a successful API call.
    // refresh=true means a new visit was created (reschedule) — trigger a full sync.
    // refresh=false means the visit was completed/cancelled — also sync to remove it.
    const handleOverdueSuccess = ({ refresh }) => {
        setOverdueModal({ open: false, scenario: null, visit: null, absentDate: null, absentVisitCount: 0 });
        // Always re-fetch — overdue list needs to shrink
        if (onSync) onSync();
    };

    // Handle filter mode change
    const handleFilterChange = (newFilterMode) => {
        setFilterMode(newFilterMode);
        // Notify parent component to refetch visits with new filter
        if (onFilterChange) {
            onFilterChange(newFilterMode);
        }
    };

    // Handle view mode change
    const handleViewModeChange = (newViewMode) => {
        // Notify parent component to refetch visits with new view mode
        if (onViewModeChange) {
            onViewModeChange(newViewMode);
        }
    };

    // Reading scrollLeft/clientWidth forces a synchronous layout. Doing that on
    // every scroll event meant a reflow per frame of the swipe; coalescing into
    // one animation frame keeps the gesture on the compositor.
    const scrollFrame = useRef(null);
    const handleCarouselScroll = useCallback(() => {
        if (scrollFrame.current !== null) return;
        scrollFrame.current = requestAnimationFrame(() => {
            scrollFrame.current = null;
            if (!carouselRef.current) return;
            const { scrollLeft, clientWidth } = carouselRef.current;
            if (clientWidth > 0) setCurrentCardIndex(Math.round(scrollLeft / clientWidth));
        });
    }, []);

    useEffect(() => () => {
        if (scrollFrame.current !== null) cancelAnimationFrame(scrollFrame.current);
    }, []);

    const scrollToCard = useCallback((index) => {
        if (!carouselRef.current) return;
        carouselRef.current.scrollTo({ left: index * carouselRef.current.clientWidth, behavior: 'smooth' });
    }, []);

    // Reset carousel to first card when filter or view changes
    useEffect(() => {
        setCurrentCardIndex(0);
        if (carouselRef.current) carouselRef.current.scrollTo({ left: 0, behavior: 'instant' });
    }, [filterMode, viewMode]);

    const getPriorityStyles = (priority, visitState) => {
        if (visitState === 'checked_in') return {
            border: 'border-emerald-500/50', bg: 'bg-emerald-500/5',
            strip: 'bg-gradient-to-r from-emerald-500 to-teal-500',
            number: 'from-emerald-500 to-teal-600',
        };
        if (priority === 'high') return {
            border: 'border-orange-500/40', bg: 'bg-orange-500/5',
            strip: 'bg-gradient-to-r from-orange-500 to-red-500',
            number: 'from-orange-500 to-red-600',
        };
        if (priority === 'medium') return {
            border: 'border-blue-500/40', bg: 'bg-blue-500/5',
            strip: 'bg-gradient-to-r from-blue-500 to-indigo-500',
            number: 'from-blue-500 to-indigo-600',
        };
        return {
            border: 'border-gray-600/40', bg: 'bg-gray-800',
            strip: 'bg-gradient-to-r from-gray-600 to-gray-700',
            number: 'from-gray-600 to-gray-700',
        };
    };

    return (
        <div
            data-testid="visits-panel"
            data-view-mode={viewMode}
            data-filter-mode={filterMode}
            className={panelMode
                ? "h-full w-full bg-gray-900 flex flex-col overflow-hidden"
                : "fixed top-20 right-4 md:top-44 md:right-6 bg-gray-900 rounded-xl shadow-2xl w-[90vw] md:w-80 max-h-[calc(var(--app-height)_-_180px)] overflow-hidden z-30 border border-gray-700 flex flex-col"
            }
        >
            {/* ── HEADER ROW 1: Title bar ── */}
            <div className="flex-shrink-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-4 py-3 text-white">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-white/15 ring-1 ring-white/30 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4" />
                    </div>
                    <span className="font-bold text-base flex-1 truncate">My Visits</span>
                    {geofenceActive && (
                        <span className="flex items-center gap-1 text-[10px] font-semibold bg-white/20 px-1.5 py-0.5 rounded-full flex-shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse inline-block" />
                            GPS
                        </span>
                    )}
                    {viewMode === 'route' && sortedVisits.length > 1 && (
                        <span className="text-sm font-bold text-white/70 flex-shrink-0 tabular-nums">
                            {currentCardIndex + 1} / {sortedVisits.length}
                        </span>
                    )}
                    <button aria-label="Close visits panel" onClick={onClose} data-testid="visits-panel-close"
                        className="flex-shrink-0 p-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* ── HEADER ROW 2: Filters + controls ── */}
            {!hideInternalFilters && (
                <div className="flex-shrink-0 bg-gray-800 border-b border-gray-700 px-3 py-2.5 space-y-2">
                    {/* Wraps instead of clipping. On a 390px phone the route-mode row
                        needs about 436px once Optimize appears (it shows from two
                        visits on), which cut History in half and pushed Search off
                        the screen entirely. The actions keep to the right on one
                        line when they fit and drop to a second line when they do
                        not, so nothing is ever unreachable. */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {viewMode === 'route' ? (
                            <>
                                <button onClick={() => handleFilterChange('today')} data-testid="visit-filter-today"
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors whitespace-nowrap ${filterMode === 'today' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-gray-200'}`}>
                                    📍 Today{sortedVisits.length > 0 ? ` · ${sortedVisits.length}` : ''}
                                </button>
                                <button aria-label="Show overdue visits" onClick={() => handleFilterChange('overdue')} data-testid="visit-filter-overdue"
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors whitespace-nowrap ${filterMode === 'overdue' ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-gray-200'}`}>
                                    ⚠️{overdueCount > 0 ? ` · ${overdueCount}` : ''}
                                </button>
                                <button aria-label="Show scheduled visits" onClick={() => handleFilterChange('scheduled')} data-testid="visit-filter-scheduled"
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors whitespace-nowrap ${filterMode === 'scheduled' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-gray-200'}`}>
                                    📅
                                </button>
                            </>
                        ) : (
                            /* History mode drops the filters, which otherwise left the
                               row as two buttons floating against 270px of nothing —
                               and no indication of which mode you were in, since the
                               toggle reads "Route" here. */
                            <span className="text-xs font-bold text-gray-400 whitespace-nowrap px-1">
                                📜 Past visits
                            </span>
                        )}
                        <div className="flex items-center gap-1.5 ml-auto">
                            {viewMode === 'route' && sortedVisits.length >= 2 && (
                                <button onClick={onOptimizeRoute} disabled={isOptimizing} data-testid="optimize-route-btn" data-optimizing={isOptimizing ? 'true' : 'false'}
                                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold transition-colors flex-shrink-0 ${isOptimizing ? 'bg-gray-700 text-gray-500' : 'bg-indigo-600 hover:bg-indigo-500 text-white'}`}>
                                    <Navigation className="w-3 h-3" />
                                    {isOptimizing ? '…' : optimizedRoute ? 'Re-opt' : 'Optimize'}
                                </button>
                            )}
                            <button onClick={() => handleViewModeChange(viewMode === 'route' ? 'history' : 'route')}
                                data-testid={viewMode === 'route' ? 'visit-view-history' : 'visit-view-route'}
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-bold transition-colors flex-shrink-0 ${viewMode === 'history' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-gray-200'}`}>
                                {viewMode === 'history' ? '🚀 Route' : '📜 History'}
                            </button>
                            {/* Matches the pills: 28px tall and round, not 26px and
                                rounded-lg, which sat a pixel off the row's baseline. */}
                            <button onClick={() => setShowSearch(p => !p)} aria-label="Search visits"
                                className={`p-1.5 rounded-full transition-colors flex-shrink-0 ${showSearch ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-gray-200'}`}>
                                <Search className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    {showSearch && (
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                            <input type="text" placeholder="Search by name or address..." value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)} data-testid="visit-search" autoFocus
                                className="w-full pl-9 pr-4 py-2 bg-gray-700 border border-gray-600 text-gray-200 placeholder-gray-500 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                        </div>
                    )}
                </div>
            )}

            {/* ── Desktop actions row ──
                The desktop right-panel hides the internal filter bar (filters
                come from the parent), but that bar also held the route actions
                (Optimize + Route/History toggle). Render just those actions so
                desktop users aren't stuck without them. */}
            {hideInternalFilters && (
                <div className="flex-shrink-0 bg-gray-800 border-b border-gray-700 px-3 py-2 flex flex-wrap items-center justify-end gap-1.5">
                    {viewMode === 'route' && sortedVisits.length >= 2 && (
                        <button onClick={onOptimizeRoute} disabled={isOptimizing} data-testid="optimize-route-btn" data-optimizing={isOptimizing ? 'true' : 'false'}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold transition-colors flex-shrink-0 ${isOptimizing ? 'bg-gray-700 text-gray-500' : 'bg-indigo-600 hover:bg-indigo-500 text-white'}`}>
                            <Navigation className="w-3 h-3" />
                            {isOptimizing ? '…' : optimizedRoute ? 'Re-opt' : 'Optimize'}
                        </button>
                    )}
                    <button onClick={() => handleViewModeChange(viewMode === 'route' ? 'history' : 'route')}
                        data-testid={viewMode === 'route' ? 'visit-view-history' : 'visit-view-route'}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-bold transition-colors flex-shrink-0 ${viewMode === 'history' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-gray-200'}`}>
                        {viewMode === 'history' ? '🚀 Route' : '📜 History'}
                    </button>
                </div>
            )}

            {/* ── CONTENT ── */}
            <div className="flex-1 overflow-hidden bg-gray-900 flex flex-col">
                {visitsLoading ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
                        <div className="w-8 h-8 border-[3px] border-gray-700 border-t-blue-500 rounded-full animate-spin" />
                        <span className="text-sm font-medium">Loading…</span>
                    </div>

                ) : filterMode === 'overdue' && viewMode === 'route' ? (
                    <div className="flex-1 overflow-y-auto scroll-pane">
                        {sortedOverdueList.length === 0 ? (
                            <div className="flex flex-col items-center justify-center text-center py-10 px-5 mx-3 mt-3 bg-gray-700/30 rounded-xl text-gray-500">
                                <div className="text-3xl mb-2">✅</div>
                                <p className="font-semibold text-gray-400 mb-1">No overdue visits</p>
                                <p className="text-sm">You're all caught up!</p>
                            </div>
                        ) : (() => {
                            const dateGroups = {};
                            sortedOverdueList.forEach(v => {
                                const d = v.scheduled_date || '__no_date__';
                                if (!dateGroups[d]) dateGroups[d] = [];
                                dateGroups[d].push(v);
                            });
                            const renderedBanners = new Set();
                            const nodes = [];
                            sortedOverdueList.forEach((item, index) => {
                                const d = item.scheduled_date;
                                if (d && dateGroups[d].length >= 2 && !renderedBanners.has(d)) {
                                    renderedBanners.add(d);
                                    const displayD = new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
                                    const count = dateGroups[d].length;
                                    nodes.push(
                                        <div key={`bulk-banner-${d}`} className="mx-3 mt-3 bg-yellow-900/30 border border-yellow-700/50 rounded-xl px-3.5 py-3 flex items-center justify-between gap-2.5 flex-wrap">
                                            <span className="text-sm font-semibold text-yellow-300">Were you absent on {displayD}?</span>
                                            <button onClick={() => openBulkAbsentModal(d, count)} className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-500 text-white border-none rounded-lg text-xs font-bold cursor-pointer transition-colors whitespace-nowrap" style={{ minHeight: '36px' }}>
                                                Yes, handle all ({count})
                                            </button>
                                        </div>
                                    );
                                }
                                nodes.push(
                                    <div key={item.id} className="mx-3 mt-3">
                                        <OverdueCard item={item} index={index} getDaysOverdue={getDaysOverdue} onItemClick={onItemClick} onOpenOverdueModal={openOverdueModal} canDo={canDo} />
                                    </div>
                                );
                            });
                            return <div className="pb-3">{nodes}</div>;
                        })()}
                    </div>

                ) : sortedVisits.length === 0 ? (
                    <div className="flex-1 overflow-y-auto scroll-pane">
                        <div className="flex flex-col items-center justify-center py-12 px-4 mx-3 mt-3 bg-gray-700/30 rounded-xl">
                            <div className="text-6xl mb-4">📭</div>
                            <h4 className="text-lg font-semibold text-gray-300 mb-2">No visits in this view</h4>
                            <p className="text-sm text-gray-500 text-center">
                                {filterMode === 'today' && "No visits scheduled for today. Check ⚠️ Overdue or 📅 Scheduled."}
                                {filterMode === 'scheduled' && "No future visits scheduled yet."}
                                {viewMode === 'history' && "No past visits yet."}
                            </p>
                        </div>
                    </div>

                ) : viewMode === 'history' ? (
                    <div className="flex-1 overflow-y-auto scroll-pane pb-3">
                        {sortedVisits.map((item, index) => {
                            const vsb = getVisitStateBadge(item.visit_state);
                            const darkMap = { 'bg-yellow-100 text-yellow-700': 'bg-yellow-900/40 text-yellow-300', 'bg-blue-100 text-blue-700': 'bg-blue-900/40 text-blue-300', 'bg-green-100 text-green-700': 'bg-green-900/40 text-green-300', 'bg-gray-100 text-gray-700': 'bg-gray-700 text-gray-400', 'bg-red-100 text-red-700': 'bg-red-900/40 text-red-300' };
                            const darkClass = darkMap[`${vsb.bg} ${vsb.text}`] || 'bg-gray-700 text-gray-400';
                            return (
                                <div key={item.id} data-testid="visit-card" data-visit-id={item.id} data-entity-global-id={item.entity_global_id} data-visit-state={item.visit_state}
                                    className="flex items-center gap-2.5 px-3 py-3 border-b border-gray-800 cursor-pointer hover:bg-gray-800/60 transition-colors"
                                    onClick={() => onItemClick && onItemClick(item)}>
                                    <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-400 flex-shrink-0">{index + 1}</div>
                                    <div className="flex-1 min-w-0">
                                        <span data-testid="visit-card-name" className="font-semibold text-sm text-gray-200 truncate block">{sanitize(item.entity_name)}</span>
                                    </div>
                                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${darkClass}`}>{vsb.label}</span>
                                    {/* When the visit happened — meaningful in history (replaces
                                        the "distance from current location" which is irrelevant for past visits). */}
                                    {(() => {
                                        const visitDate = item.completed_at || item.actual_visit_date || item.scheduled_date;
                                        return visitDate
                                            ? <span className="text-xs text-gray-400 font-medium flex-shrink-0">{formatDate(visitDate)}</span>
                                            : null;
                                    })()}
                                    <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
                                </div>
                            );
                        })}
                    </div>

                ) : (
                    /* ── ROUTE: full-width swipeable card carousel ── */
                    <div
                        ref={carouselRef}
                        onScroll={handleCarouselScroll}
                        className="flex-1 flex overflow-x-auto scroll-pane snap-x snap-mandatory"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                    >
                        {sortedVisits.map((item, index) => {
                            const ps = getPriorityStyles(item.priority, item.visit_state);
                            return (
                                <div key={item.id} className="flex-none w-full snap-start overflow-y-auto scroll-pane">
                                    <div className={`mx-3 mt-3 mb-3 rounded-xl border ${ps.border} overflow-hidden`}
                                        data-testid="visit-card" data-visit-id={item.id} data-entity-global-id={item.entity_global_id} data-visit-state={item.visit_state}>
                                        <div className={`h-1.5 w-full ${ps.strip}`} />
                                        <div className={`p-4 ${ps.bg}`}>
                                            {/* Name row */}
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-2.5 min-w-0 flex-1 cursor-pointer" onClick={() => onItemClick && onItemClick(item)}>
                                                    <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${ps.number} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                                                        {index + 1}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h3 data-testid="visit-card-name" className="font-bold text-gray-100 text-base leading-tight">{sanitize(item.entity_name)}</h3>
                                                        <span className="text-xs text-gray-400">
                                                            {item.visit_state === 'checked_in' ? '✓ In progress' : (item.visit_intent || 'planned visit').replace(/_/g, ' ')}
                                                        </span>
                                                    </div>
                                                </div>
                                                {item.visit_state !== 'checked_in' && canDo('remove', item) && (
                                                    <button aria-label="Remove from today's route" onClick={(e) => { e.stopPropagation(); onRemove(item.entity_global_id); }} data-testid="visit-card-remove"
                                                        className="text-gray-500 hover:text-red-400 transition-colors p-1 flex-shrink-0">
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>

                                            {/* Metadata */}
                                            <div className="flex items-center flex-wrap gap-1.5 text-xs mb-4">
                                                {formatKm(item.distance) && (
                                                    <span className="text-blue-400 font-semibold flex items-center gap-0.5"><Navigation className="w-3 h-3" />{formatKm(item.distance)}</span>
                                                )}
                                                <span>{item.priority === 'high' ? '🔥🔥🔥' : item.priority === 'medium' ? '⚡⚡' : '❄️'}</span>
                                                <span className={`px-1.5 py-0.5 rounded-full font-semibold ${item.entity_status === 'at_risk' ? 'bg-orange-900/40 text-orange-300' : item.entity_status === 'churned' ? 'bg-red-900/40 text-red-300' : item.entity_status === 'new' ? 'bg-blue-900/40 text-blue-300' : item.entity_status === 'planned' ? 'bg-purple-900/40 text-purple-300' : item.entity_status === 'contacted' ? 'bg-sky-900/40 text-sky-300' : item.entity_status === 'won' ? 'bg-green-900/40 text-green-300' : item.entity_status === 'lost' ? 'bg-gray-700 text-gray-400' : item.entity_status === 'active' ? 'bg-emerald-900/40 text-emerald-300' : item.entity_status === 'inactive' ? 'bg-gray-700 text-gray-400' : 'bg-blue-900/40 text-blue-300'}`}>
                                                    {(item.entity_status || '').toUpperCase().replace('_', ' ')}
                                                </span>
                                                {item.visit_state && item.visit_state !== 'planned' && (() => {
                                                    const vsb = getVisitStateBadge(item.visit_state);
                                                    const darkMap = { 'bg-yellow-100 text-yellow-700': 'bg-yellow-900/40 text-yellow-300', 'bg-blue-100 text-blue-700': 'bg-blue-900/40 text-blue-300', 'bg-green-100 text-green-700': 'bg-green-900/40 text-green-300', 'bg-gray-100 text-gray-700': 'bg-gray-700 text-gray-400', 'bg-red-100 text-red-700': 'bg-red-900/40 text-red-300' };
                                                    const darkClass = darkMap[`${vsb.bg} ${vsb.text}`] || 'bg-gray-700 text-gray-400';
                                                    return <span className={`px-1.5 py-0.5 rounded-full font-semibold ${darkClass}`}>{vsb.label}</span>;
                                                })()}
                                                {item.scheduled_date && (
                                                    <span className="flex items-center gap-0.5 text-purple-400 font-medium"><Calendar className="w-3 h-3" />{formatDate(item.scheduled_date)}</span>
                                                )}
                                                {item.claimed_at && (
                                                    <span className="flex items-center gap-0.5 text-gray-500"><Clock className="w-3 h-3" />{getDaysSinceAdded(item.claimed_at) === 0 ? 'today' : `${getDaysSinceAdded(item.claimed_at)}d ago`}</span>
                                                )}
                                                {item.visit_state === 'planned' && getDaysOverdue(item.scheduled_date) >= 1 && getOverdueBadge(getDaysOverdue(item.scheduled_date)) && (() => {
                                                    const daysOv = getDaysOverdue(item.scheduled_date);
                                                    const darkBg = daysOv > 14 ? 'bg-red-900/40 text-red-300' : daysOv > 7 ? 'bg-orange-900/40 text-orange-300' : 'bg-yellow-900/40 text-yellow-300';
                                                    const badge = getOverdueBadge(daysOv);
                                                    return <span className={`flex items-center gap-0.5 ${darkBg} px-1.5 py-0.5 rounded-full font-semibold`}><AlertCircle className="w-3 h-3" />{badge.label}</span>;
                                                })()}
                                                {item.rescheduled_count > 0 && (
                                                    <span className="flex items-center gap-0.5 bg-purple-900/40 text-purple-300 px-1.5 py-0.5 rounded-full font-medium"><RefreshCw className="w-3 h-3" />x{item.rescheduled_count}</span>
                                                )}
                                                {isStale(item.claimed_at) && getDaysOverdue(item.scheduled_date) < 1 && (
                                                    <span className="flex items-center gap-0.5 bg-orange-900/40 text-orange-300 px-1.5 py-0.5 rounded-full font-semibold"><AlertCircle className="w-3 h-3" />Stale</span>
                                                )}
                                            </div>

                                            {/* Actions */}
                                            <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                                                {item.visit_state === 'planned' && onCheckIn && (
                                                    <button onClick={() => onCheckIn(item.id)} data-testid="visit-action-checkin"
                                                        className="w-full flex items-center justify-center gap-2 px-3 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg text-sm font-semibold shadow-md transition-all">
                                                        <PlayCircle className="w-4 h-4" />Check In
                                                    </button>
                                                )}
                                                {item.visit_state === 'planned' && onReschedule && canDo('reschedule', item) && (
                                                    rescheduleVisitId === item.id ? (
                                                        <div className="flex items-center gap-2" data-testid="reschedule-flow">
                                                            <input type="date" value={rescheduleDate} min={new Date().toISOString().split('T')[0]} onChange={(e) => setRescheduleDate(e.target.value)} data-testid="reschedule-date-input" className="flex-1 min-w-0 px-2 py-2 bg-gray-700 border border-gray-600 text-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 appearance-none" />
                                                            <button onClick={() => handleRescheduleSubmit(item.id)} disabled={!rescheduleDate} data-testid="reschedule-save" className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50">Save</button>
                                                            <button aria-label="Cancel reschedule" onClick={() => { setRescheduleVisitId(null); setRescheduleDate(''); }} data-testid="reschedule-cancel" className="px-2 py-2 text-gray-500 hover:text-gray-300"><X className="w-4 h-4" /></button>
                                                        </div>
                                                    ) : (
                                                        <button onClick={() => item.customer_confirmed ? setRescheduleModalVisit(item) : setRescheduleVisitId(item.id)} data-testid="visit-action-reschedule"
                                                            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-900/30 hover:bg-purple-900/50 text-purple-300 rounded-lg text-xs font-semibold border border-purple-800 transition-colors">
                                                            <Calendar className="w-4 h-4" />Reschedule Visit
                                                        </button>
                                                    )
                                                )}
                                                {item.visit_state === 'checked_in' && onCompleteVisit && (
                                                    <div className="space-y-2">
                                                        {completeError[item.id] && (
                                                            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg" data-testid="visit-complete-error">
                                                                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                                                <span className="text-xs text-red-400">Add notes or record a voice memo before completing</span>
                                                            </div>
                                                        )}
                                                        <button onClick={() => handleCompleteWithValidation(item.id, item.entity_global_id)} data-testid="visit-action-complete"
                                                            className="w-full flex items-center justify-center gap-2 px-3 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg text-sm font-semibold shadow-md transition-all">
                                                            <CheckCircle className="w-4 h-4" />Complete Visit
                                                        </button>
                                                    </div>
                                                )}
                                                {undoCountdowns[item.id] > 0 && (
                                                    <button onClick={() => onUndo(item.id)} data-testid="visit-action-undo" data-undo-seconds-remaining={undoCountdowns[item.id]}
                                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-900/30 hover:bg-orange-900/50 text-orange-300 rounded-lg text-xs font-semibold border border-orange-800/50">
                                                        <span className="font-mono" data-testid="undo-countdown">{undoCountdowns[item.id]}s</span><span>Undo</span>
                                                    </button>
                                                )}
                                                {item.visit_state === 'planned' && skipVisitId === item.id && (
                                                    <div className="space-y-2 bg-gray-900/60 p-3 rounded-lg border border-gray-600" data-testid="skip-flow">
                                                        {skipStep === 'reason' ? (
                                                            <>
                                                                <p className="text-xs font-semibold text-gray-300">Why are you skipping?</p>
                                                                <select value={skipReason} onChange={(e) => setSkipReason(e.target.value)} data-testid="skip-reason-select" className="w-full px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 text-gray-200 rounded-lg">
                                                                    <option value="">Select a reason...</option>
                                                                    {SKIP_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                                                </select>
                                                                <div className="flex gap-2">
                                                                    <button onClick={handleSkipReasonNext} disabled={!skipReason} data-testid="skip-next" className="flex-1 px-3 py-1.5 bg-gray-500 hover:bg-gray-400 text-white text-xs rounded-lg font-semibold disabled:opacity-40">Next →</button>
                                                                    <button onClick={() => { setSkipVisitId(null); setSkipReason(''); setSkipStep('reason'); }} data-testid="skip-back" className="px-3 py-1.5 text-gray-400 hover:text-gray-200 text-xs">Back</button>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p className="text-xs font-semibold text-gray-300">Let the customer know?</p>
                                                                <div className="flex gap-2">
                                                                    <a href={`tel:${item.entity_phone || ''}`} className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-lg font-semibold text-center" onClick={() => handleSkipSubmit(item.id)} data-testid="skip-call">📞 Call</a>
                                                                    <button onClick={() => handleSkipSubmit(item.id)} data-testid="skip-confirm" className="flex-1 px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-gray-50 text-xs rounded-lg font-semibold">Skip anyway</button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                                {(item.visit_state === 'planned' || item.visit_state === 'checked_in') && cancelVisitId === item.id && (
                                                    <div className="space-y-2 bg-gray-900/60 p-3 rounded-lg border border-red-800" data-testid="cancel-flow">
                                                        {cancelStep === 'reason' ? (
                                                            <>
                                                                <p className="text-xs font-semibold text-red-400">Reason for cancellation?</p>
                                                                <select value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason-select" className="w-full px-2 py-1.5 text-xs bg-gray-700 border border-red-700 text-gray-200 rounded-lg">
                                                                    <option value="">Select a reason...</option>
                                                                    {CANCEL_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                                                </select>
                                                                <div className="flex gap-2">
                                                                    <button onClick={handleCancelReasonNext} disabled={!cancelReason} data-testid="cancel-next" className="flex-1 px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white text-xs rounded-lg font-semibold disabled:opacity-40">Next →</button>
                                                                    <button onClick={() => { setCancelVisitId(null); setCancelReason(''); setCancelStep('reason'); }} data-testid="cancel-back" className="px-3 py-1.5 text-gray-400 hover:text-gray-200 text-xs">Back</button>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p className="text-xs font-semibold text-red-400">Let the customer know?</p>
                                                                <div className="flex gap-2">
                                                                    <a href={`tel:${item.entity_phone || ''}`} className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-lg font-semibold text-center" onClick={() => handleCancelSubmit(item.id)} data-testid="cancel-call">📞 Call</a>
                                                                    <button onClick={() => handleCancelSubmit(item.id)} data-testid="cancel-confirm" className="flex-1 px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white text-xs rounded-lg font-semibold">Cancel anyway</button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                                {expandedNotes[item.id] && (
                                                    <div className="space-y-2">
                                                        <textarea value={visitNotes[item.id] || ''} onChange={(e) => handleNotesChange(item.id, e.target.value)} placeholder="Add notes about this visit..." data-testid="visit-notes-textarea"
                                                            className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 text-gray-200 placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none" rows={3} />
                                                        <button onClick={() => handleSaveNotes(item.id, visitNotes[item.id] || '')} disabled={saveStatus[item.id] === 'saving'} data-testid="visit-notes-save"
                                                            className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg ${saveStatus[item.id] === 'saving' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : saveStatus[item.id] === 'saved' ? 'bg-green-600 text-white' : saveStatus[item.id] === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white hover:bg-emerald-500'}`}>
                                                            <Save className="w-3 h-3" />
                                                            <span>{saveStatus[item.id] === 'saving' ? 'Saving...' : saveStatus[item.id] === 'saved' ? 'Saved!' : saveStatus[item.id] === 'error' ? 'Error - Try Again' : 'Save Notes'}</span>
                                                        </button>
                                                    </div>
                                                )}
                                                {/* Utility row */}
                                                <div className="flex gap-1.5">
                                                    <button onClick={() => handleNavigate(item)} data-testid="visit-action-navigate"
                                                        className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 bg-blue-900/30 hover:bg-blue-900/50 text-blue-300 rounded-lg text-[10px] font-semibold border border-blue-800/50 min-h-[48px]">
                                                        <Navigation className="w-4 h-4" /><span>Go</span>
                                                    </button>
                                                    {/* Notes always available while checked-in (needed to complete);
                                                        otherwise gated by the 'edit' permission. */}
                                                    {(item.visit_state === 'checked_in' || canDo('edit', item)) && (
                                                        <button onClick={() => setExpandedNotes(prev => ({ ...prev, [item.id]: !prev[item.id] }))} data-testid="visit-notes-toggle"
                                                            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 rounded-lg text-[10px] font-semibold border min-h-[48px] ${expandedNotes[item.id] ? 'bg-purple-900/50 text-purple-200 border-purple-700' : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600'}`}>
                                                            <MessageSquare className="w-4 h-4" /><span>Notes</span>
                                                        </button>
                                                    )}
                                                    <div className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1 min-h-[48px]">
                                                        <VoiceRecorder customerId={item.entity_type === 'customer' ? parseInt(item.entity_global_id.split('_')[1]) : undefined} leadId={item.entity_type === 'lead' ? parseInt(item.entity_global_id.split('_')[1]) : undefined} visitId={item.id} onUploadSuccess={(data) => handleVoiceUpload(item.entity_global_id, item.id, data)} />
                                                        <span className="text-[10px] text-gray-500 font-semibold">Voice</span>
                                                    </div>
                                                    {item.visit_state === 'planned' && skipVisitId !== item.id && canDo('skip', item) && (
                                                        <button onClick={() => { setSkipVisitId(item.id); setSkipStep('reason'); }} data-testid="visit-action-skip"
                                                            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-[10px] font-semibold border border-gray-600 min-h-[48px]">
                                                            <X className="w-4 h-4" /><span>Skip</span>
                                                        </button>
                                                    )}
                                                    {(item.visit_state === 'planned' || item.visit_state === 'checked_in') && cancelVisitId !== item.id && canDo('cancel', item) && (
                                                        <button onClick={() => { setCancelVisitId(item.id); setCancelStep('reason'); }} data-testid="visit-action-cancel"
                                                            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg text-[10px] font-semibold border border-red-800 min-h-[48px]">
                                                            <AlertCircle className="w-4 h-4" /><span>Cancel</span>
                                                        </button>
                                                    )}
                                                </div>
                                                {feedbackData[item.entity_global_id] && (
                                                    <div className="bg-blue-900/20 border border-blue-800/40 rounded-lg p-2 space-y-1">
                                                        <div className="flex items-start gap-2">
                                                            <MessageSquare className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                                                            <div className="flex-1 space-y-1">
                                                                <p className="text-xs text-gray-300 leading-relaxed">{sanitize(feedbackData[item.entity_global_id].summary)}</p>
                                                                <div className="flex flex-wrap gap-2 text-xs">
                                                                    <span className={`px-2 py-0.5 rounded-full font-semibold ${feedbackData[item.entity_global_id].sentiment === 'positive' ? 'bg-green-900/40 text-green-300' : feedbackData[item.entity_global_id].sentiment === 'negative' ? 'bg-red-900/40 text-red-300' : 'bg-gray-700 text-gray-400'}`}>
                                                                        {feedbackData[item.entity_global_id].sentiment === 'positive' ? '😊' : feedbackData[item.entity_global_id].sentiment === 'negative' ? '😞' : '😐'}{feedbackData[item.entity_global_id].sentiment}
                                                                    </span>
                                                                    {feedbackData[item.entity_global_id].order_amount > 0 && (
                                                                        <span className="px-2 py-0.5 rounded-full font-semibold bg-emerald-900/40 text-emerald-300">💰 ¥{feedbackData[item.entity_global_id].order_amount}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Dot indicator — carousel position */}
            {viewMode === 'route' && sortedVisits.length > 1 && !visitsLoading && filterMode !== 'overdue' && (
                <div className="flex-shrink-0 flex justify-center items-center gap-2 py-2.5 bg-gray-900 border-t border-gray-800">
                    {sortedVisits.map((_, i) => (
                        <button key={i} onClick={() => scrollToCard(i)}
                            aria-label={`Go to visit ${i + 1} of ${sortedVisits.length}`}
                            aria-current={i === currentCardIndex}
                            className={`rounded-full transition-all duration-200 ${i === currentCardIndex ? 'w-5 h-2 bg-blue-400' : 'w-2 h-2 bg-gray-600 hover:bg-gray-500'}`} />
                    ))}
                </div>
            )}

            {/* ── FOOTER — Clear button ── */}
            {viewMode === 'route' && (filterMode === 'overdue' ? sortedOverdueList.length > 0 : sortedVisits.length > 0) && (
                <div className="flex-shrink-0 p-3 bg-gray-800 border-t border-gray-700">
                    <button onClick={() => setShowClearConfirm(true)} data-testid="clear-visits-btn"
                        className="w-full px-4 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-800/50 rounded-lg font-semibold transition-colors text-sm">
                        {filterMode === 'today' ? "Clear Today's Visits" : filterMode === 'overdue' ? 'Clear Overdue Visits' : filterMode === 'scheduled' ? 'Clear Scheduled Visits' : 'Clear Visits'}
                    </button>
                    {showClearConfirm && (
                        <div className="mt-2 bg-red-900/30 border border-red-800/50 rounded-lg p-3" data-testid="clear-visits-confirm">
                            <p className="text-sm text-red-300 font-medium mb-2">Clear {sortedVisits.length} {filterMode === 'today' ? "today's" : filterMode} visits? This cannot be undone.</p>
                            <div className="flex gap-2">
                                <button onClick={() => { onClear(filterMode); setShowClearConfirm(false); }} data-testid="clear-visits-yes" className="flex-1 px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white text-sm rounded-lg font-semibold">Yes, Clear</button>
                                <button onClick={() => setShowClearConfirm(false)} data-testid="clear-visits-no" className="flex-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg font-semibold border border-gray-600">Cancel</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <OverdueResolutionModal isOpen={overdueModal.open} onClose={() => setOverdueModal(s => ({ ...s, open: false }))} scenario={overdueModal.scenario} visit={overdueModal.visit} absentDate={overdueModal.absentDate} absentVisitCount={overdueModal.absentVisitCount} onSuccess={handleOverdueSuccess} />

            {/* Reschedule via the full flow (date + time + confirmation) — only for
                customer-confirmed appointments. Ad-hoc visits use the inline date picker above. */}
            <AddVisitModal
                mode="reschedule"
                isOpen={!!rescheduleModalVisit}
                entityName={rescheduleModalVisit?.entity_name}
                initialValues={rescheduleModalVisit ? { date: rescheduleModalVisit.scheduled_date, confirmed: rescheduleModalVisit.customer_confirmed } : null}
                onClose={() => setRescheduleModalVisit(null)}
                onConfirm={(data) => {
                    if (onReschedule && rescheduleModalVisit) onReschedule(rescheduleModalVisit.id, data);
                    setRescheduleModalVisit(null);
                }}
            />
        </div>
    );
};

// Shallow memo on the key props that drive re-renders. Callback props (onRemove, onClose, etc.)
// will still break memoization if App.js doesn't useCallback them, but we gate on the
// data-driven props so at minimum identical-data renders are skipped.
export default memo(TodaysVisits, (prev, next) => {
    return (
        prev.visitList === next.visitList &&
        prev.overdueList === next.overdueList &&
        prev.visitsLoading === next.visitsLoading &&
        prev.visitPermissions === next.visitPermissions &&
        prev.viewMode === next.viewMode &&
        prev.parentFilterMode === next.parentFilterMode &&
        prev.overdueCount === next.overdueCount &&
        prev.isOptimizing === next.isOptimizing &&
        prev.optimizedRoute === next.optimizedRoute &&
        prev.showRouteOnMap === next.showRouteOnMap &&
        prev.undoableActions === next.undoableActions &&
        prev.onRemove === next.onRemove &&
        prev.onClear === next.onClear &&
        prev.onSync === next.onSync &&
        prev.onClose === next.onClose &&
        prev.onVoiceUploadSuccess === next.onVoiceUploadSuccess &&
        prev.onItemClick === next.onItemClick &&
        prev.onCheckIn === next.onCheckIn &&
        prev.onCompleteVisit === next.onCompleteVisit &&
        prev.onFilterChange === next.onFilterChange &&
        prev.onViewModeChange === next.onViewModeChange &&
        prev.onUndo === next.onUndo &&
        prev.onOptimizeRoute === next.onOptimizeRoute &&
        prev.onToggleRouteOnMap === next.onToggleRouteOnMap &&
        prev.onReschedule === next.onReschedule &&
        prev.onLeadStatusChange === next.onLeadStatusChange
    );
});
